import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ItemCreation, ItemCreationToolTip } from '~/Components/ItemCreation';
import ModalComponent from '~/Components/Modal/Modal.component';
import { Button, Dots, Typography } from '~/UIKit';
import { ExtendedEventSeries } from '../../extendedEventSeries.model';
import { EventScheduleTimeSeriesItemStyles } from './EventScheduleTimeSeriesItem.styles';
import { useEventScheduleTimeSeriesItem } from './useEventScheduleTimeSeriesItem';
import { EventType } from '~/Models/DTO/Event.model';
import { useCheckEventSeriesBookings } from '../useCheckEventSeriesBookings';
import { AccessList, useDateFns, useHasAccess } from '~/Hooks';
import { Error } from '~/Components';
import { TopToolbar } from '~/UIKit/TopToolbar';

export const EventScheduleTimeSeriesItem = ({
  onUpdate,
  timeSeries,
  onDelete,
  onUpdateSingleValue,
  disabled,
  eventType,
}: {
  timeSeries: ExtendedEventSeries;
  onUpdate: (eventSeries: ExtendedEventSeries) => void;
  onUpdateSingleValue: (name: string, value: string) => void;
  onDelete: () => void;
  disabled: boolean;
  eventType: EventType;
}) => {
  const { startOfTodayAsUTCString, addTime } = useDateFns();
  const today = startOfTodayAsUTCString();

  const { classes } = EventScheduleTimeSeriesItemStyles(disabled)();
  const { t } = useTranslation();
  const { hasAccess } = useHasAccess();
  const {
    data: anyBookings,
    isLoading: isLoadingAnyBookings,
    error: anyBookingError,
  } = useCheckEventSeriesBookings(timeSeries);
  const {
    data: futureBookings,
    isLoading: isLoadingFutureBookings,
    error: futureBookingError,
  } = useCheckEventSeriesBookings(timeSeries, true);

  const hasAnyBookings = !!timeSeries.id && anyBookings?.[0].count !== 0;
  const hasFutureBookings = !!timeSeries.id && futureBookings?.[0].count !== 0;

  const canEditABookedSchedule = hasAccess(AccessList.EDIT_SCHEDULE_WITH_BOOKINGS);
  const {
    timeRange,
    subtitle,
    timeRecurrenceName,
    listItems,
    headers,
    isEditModalOpen,
    toggleEditModalOpen,
    onCancel,
    isMobile,
    onSave,
  } = useEventScheduleTimeSeriesItem({
    timeSeries,
    onUpdate,
    onUpdateSingleValue,
    onDelete,
    eventType,
  });

  const defaultItemCreationProps = {
    listItems,
    headers,
    onSave: () => {},
    initialValues: timeSeries,
    validate: onUpdate,
    networkError: {},
    isLoading: false,
  };

  if (
    timeSeries.endDate < addTime(today, { days: -1 }).toISOString() &&
    !!timeSeries.endDate &&
    !isEditModalOpen
  ) {
    return null;
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.infoContainer}>
        <Error error={anyBookingError || futureBookingError} />
        {timeSeries.shouldDelete && (
          <Button size={isMobile ? 'small' : 'medium'} onClick={onDelete} variant="contained">
            {t('app.restore')}
          </Button>
        )}
        {hasAnyBookings && (
          <ItemCreationToolTip
            title={
              <>
                <Typography align="center" variant="h4">
                  {t('event.hasBookings.time.tooltip.title')}
                </Typography>
                {!hasFutureBookings || canEditABookedSchedule ? (
                  <Typography variant="body2">
                    {t('event.hasBookings.time.tooltip.textNeo')}
                  </Typography>
                ) : (
                  <>
                    <Typography align="left" variant="body2">
                      {t('event.hasBookings.time.tooltip.text')}
                    </Typography>
                    {[1, 3].map((optionNumber, i) => (
                      <Typography align="left" variant="body2" key={optionNumber}>
                        {i + 1}. {t(`event.hasBookings.time.tooltip.option${optionNumber}`)}
                      </Typography>
                    ))}
                  </>
                )}
              </>
            }
          />
        )}
      </div>
      {isLoadingAnyBookings || isLoadingFutureBookings ? (
        <Dots />
      ) : (
        <div
          className={classNames({
            [classes.eventScheduleItemContainer]: true,
            [classes.selected]: isEditModalOpen,
          })}
        >
          <Button
            className={classNames(classes.eventScheduleItemHeader, {
              [classes.selected]: isEditModalOpen,
            })}
            disabled={disabled}
            variant="text"
            onClick={toggleEditModalOpen}
          >
            <span
              className={classNames(classes.eventScheduleItemTitle, {
                [classes.selected]: isEditModalOpen,
              })}
            >
              {timeRecurrenceName}
              {timeRange}
            </span>
            <span
              className={classNames(classes.eventScheduleItemSubtitle, {
                [classes.selected]: isEditModalOpen,
              })}
            >
              {subtitle}
            </span>
          </Button>
          {isEditModalOpen &&
            (isMobile ? (
              <ModalComponent onClose={onCancel} open={isEditModalOpen} isFullHeight>
                <TopToolbar
                  title={t('event.edit.timeSlot')}
                  action={t('app.save')}
                  onActionTaken={onSave}
                  onBack={onCancel}
                />
                <ItemCreation skipConfirmationModal {...defaultItemCreationProps} />
              </ModalComponent>
            ) : (
              <div className={classes.eventScheduleEditorWrapperDesktop}>
                <ItemCreation skipConfirmationModal {...defaultItemCreationProps} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
